import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Box, Flex } from '@chakra-ui/react';
import { useDispatch } from 'react-redux';
import { Avatar, Modal, Select, Text } from '@kula-ai/components';
import { useShowToast } from '@/hooks/useShowToast';
import { useTzOptions } from '@/components/formBuilder/formFields/DateField/components/useTzOptions';
import { editMe } from '@/store/me/actions';
import { applyInterviewerTimezoneUpdates } from '@/api/interviewerTimezone';
import { useUpdateInterviewerTimezones } from './useUpdateInterviewerTimezones';

export interface InterviewerWithoutTimezone {
  id: number;
  name: string;
  avatar?: string;
  email?: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Interviewers missing a timezone. Length 1 renders the single-name
   *  copy ("Set timezone for X"); length >1 renders the per-row picker
   *  ("Select timezones for the following interviewers") — legacy
   *  `SchInterviewerTimezoneModal` parity. */
  interviewers: InterviewerWithoutTimezone[];
  onTimezoneUpdated: (
    _updatedInterviewers: { id: number; time_zone: string }[]
  ) => void;
}

export const InterviewerTimezoneModal = ({
  isOpen,
  onClose,
  interviewers,
  onTimezoneUpdated,
}: Props) => {
  const toast = useShowToast();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { timeZoneOptions, customFilterOption } = useTzOptions();
  const mutation = useUpdateInterviewerTimezones();

  useEffect(() => {
    if (isOpen) dispatch(editMe({}) as never);
  }, [dispatch, isOpen]);

  // Per-interviewer timezone selections. Indexed by id so adding/removing
  // rows between opens doesn't shift values onto the wrong person.
  const [tzById, setTzById] = useState<Record<number, string>>({});
  // Dialog content panel node — portal the Select menu here (kula
  // 1.10.86 recommended pattern). It escapes the modal body's overflow
  // clip yet stays inside Radix's focus-trap subtree (options stay
  // clickable) and is positioned by kula's floating-ui engine. A
  // state-setter callback ref re-renders the Selects once the node
  // exists. NOT `null` (clipped by modal body) and NOT `document.body`
  // (Radix focus-trap blocks interaction).
  const [modalContentEl, setModalContentEl] = useState<HTMLDivElement | null>(
    null
  );

  const isSingle = interviewers.length === 1;
  const singleInterviewer = isSingle ? interviewers[0] : null;

  const handleClose = useCallback(() => {
    setTzById({});
    onClose();
  }, [onClose]);

  const setOne = useCallback((id: number, value: string) => {
    setTzById((prev) => ({ ...prev, [id]: value }));
  }, []);

  const isSaveDisabled = useMemo(
    () =>
      interviewers.length === 0 ||
      interviewers.some((i) => !(tzById[i.id] && tzById[i.id].trim())),
    [interviewers, tzById]
  );

  const handleSave = useCallback(async () => {
    if (interviewers.length === 0) return;
    const usersPayload: Record<string, string> = {};
    const updates: { id: number; time_zone: string }[] = [];
    for (const it of interviewers) {
      const tz = tzById[it.id];
      if (!tz) return;
      usersPayload[`${it.id}`] = tz;
      updates.push({ id: it.id, time_zone: tz });
    }
    try {
      const result = await mutation.mutateAsync(usersPayload);
      if ((result?.errors?.length ?? 0) > 0 || !result) {
        toast({
          title: result?.errors?.[0] || 'Failed to update timezone',
          status: 'error',
        });
        return;
      }
      toast({ title: 'Timezone updated successfully', status: 'success' });
      // Record the new timezones before notifying the caller: every surface
      // that flags a missing timezone resolves against this store, so the chip
      // that opened this modal repaints immediately — including an interviewer
      // picked ad-hoc, whose option snapshot no page can patch.
      applyInterviewerTimezoneUpdates(queryClient, updates);
      onTimezoneUpdated(updates);
      handleClose();
    } catch (error: unknown) {
      const e = error as {
        response?: { data?: { errors?: string[] } };
        message?: string;
      };
      toast({
        title:
          e?.response?.data?.errors?.[0] ||
          e?.message ||
          'Failed to update timezone',
        status: 'error',
      });
    }
  }, [
    interviewers,
    tzById,
    mutation,
    onTimezoneUpdated,
    handleClose,
    toast,
    queryClient,
  ]);

  const title = useMemo(() => {
    if (isSingle && singleInterviewer) {
      return `Set timezone for ${singleInterviewer.name}`;
    }
    return 'Select timezones for the following interviewers';
  }, [isSingle, singleInterviewer]);

  const subtitle = isSingle
    ? 'Set timezone so candidates can see their availability'
    : 'Set timezones so candidates can see their availability';

  return (
    <Modal
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !mutation.isPending) handleClose();
      }}
      title={title}
      size="md"
      contentRef={setModalContentEl}
      overlayZIndex={1450}
      contentZIndex={1500}
      primaryButton={{
        label: 'Save',
        onClick: handleSave,
        isLoading: mutation.isPending,
        disabled: isSaveDisabled,
      }}
      secondaryButton={{
        label: 'Cancel',
        onClick: handleClose,
        disabled: mutation.isPending,
      }}
    >
      <Box>
        <Text variant="p" color="muted" className="mb-4">
          {subtitle}
        </Text>
        {isSingle && singleInterviewer ? (
          <Box>
            <Text variant="p" color="body" className="mb-2">
              Timezone
            </Text>
            <Select
              placeholder="Select timezone"
              value={tzById[singleInterviewer.id] ?? ''}
              options={timeZoneOptions}
              onChange={(v) =>
                setOne(singleInterviewer.id, typeof v === 'string' ? v : '')
              }
              searchable
              // Portal into the dialog content panel (kula 1.10.86):
              // escapes the modal body overflow clip, stays in Radix's
              // focus-trap, positioned/sized by kula's floating-ui.
              menuPortalTarget={modalContentEl}
              reactSelectProps={{ filterOption: customFilterOption }}
            />
          </Box>
        ) : (
          <Flex direction="column" gap="3">
            {interviewers.map((it) => (
              <Box key={it.id}>
                <Flex align="center" gap="2" mb="2">
                  <Text variant="p" color="body">
                    Timezone for
                  </Text>
                  <Avatar src={it.avatar} name={it.name} size="xs" />
                  <Text variant="p" color="body" className="font-medium">
                    {it.name}
                  </Text>
                </Flex>
                <Select
                  placeholder="Select timezone"
                  value={tzById[it.id] ?? ''}
                  options={timeZoneOptions}
                  onChange={(v) =>
                    setOne(it.id, typeof v === 'string' ? v : '')
                  }
                  searchable
                  menuPortalTarget={modalContentEl}
                  reactSelectProps={{ filterOption: customFilterOption }}
                />
              </Box>
            ))}
          </Flex>
        )}
      </Box>
    </Modal>
  );
};
